import { Mutex } from "async-mutex"
import { fetchBaseQuery, retry } from "@reduxjs/toolkit/query"
import { clearLocalAuthAction } from "@/redux/authSlice"
import {
  ACCESS_BLOCKED_ROUTE,
  accessBlockedDetailsFromData,
  accessBlockedSearch,
} from "./accessBlocked"
import { getClerkSessionToken } from "@/core/auth/clerk"
import { getLegacyAuthToken } from "@/core/auth/legacyToken"
import type { TokenInvalidResponse } from "./types"
import type { FetchArgs } from "@reduxjs/toolkit/query"

const mutex = new Mutex()
const apiBaseUrl =
  import.meta.env.VITE_BASE_API_URL ||
  (typeof window !== "undefined" &&
  window.location.hostname === "dev.dreamsender.io"
    ? "https://dev_api.dreamsender.io"
    : undefined)

const baseQuery = fetchBaseQuery({
  baseUrl: apiBaseUrl,
  prepareHeaders: async (headers) => {
    const clerkToken = await getClerkSessionToken()
    const token = getLegacyAuthToken()

    if (clerkToken) {
      headers.set("Authorization", `Bearer ${clerkToken}`)
      headers.set("X-DreamSender-Auth-Provider", "clerk")
    } else if (token) {
      headers.set("Authorization", `Bearer ${token}`)
    }

    return headers
  },
})

const getMethod = (args: string | FetchArgs) =>
  typeof args === "string" ? "GET" : args.method || "GET"

const routeToAccessBlocked = (
  details: NonNullable<ReturnType<typeof accessBlockedDetailsFromData>>,
) => {
  if (typeof window === "undefined") return
  if (window.location.pathname === ACCESS_BLOCKED_ROUTE) return

  const returnTo = `${window.location.pathname}${window.location.search}`
  const accessBlocked = { ...details, returnTo }
  const url = `${ACCESS_BLOCKED_ROUTE}?${accessBlockedSearch(accessBlocked)}`
  window.history.pushState({ accessBlocked }, "", url)
  window.dispatchEvent(
    new PopStateEvent("popstate", { state: window.history.state }),
  )
}

export const baseQueryWithReauth = retry(
  async (args: string | FetchArgs, api, extraOptions) => {
    // wait until the mutex is available without locking it
    await mutex.waitForUnlock()
    let result = await baseQuery(args, api, extraOptions)
    if (result.error && result.error.status === 401) {
      // checking whether the mutex is locked
      if (!mutex.isLocked()) {
        const release = await mutex.acquire()
        try {
          api.dispatch(clearLocalAuthAction())
        } finally {
          // release must be called once the mutex should be released again.
          release()
        }
      } else {
        // wait until the mutex is available without locking it
        await mutex.waitForUnlock()
        result = await baseQuery(args, api, extraOptions)
      }
      retry.fail(result.error)
    }
    if (result.error && result.error.status === 403) {
      // checking whether the mutex is locked
      if (!mutex.isLocked()) {
        const release = await mutex.acquire()
        try {
          if (
            (result.error.data as Partial<TokenInvalidResponse> | undefined)
              ?.error?.code === 99
          ) {
            api.dispatch(clearLocalAuthAction())
          }
        } finally {
          // release must be called once the mutex should be released again.
          release()
        }
      }
    }
    const accessBlocked = result.error
      ? accessBlockedDetailsFromData(
          "data" in result.error ? result.error.data : undefined,
          result.error.status,
        )
      : undefined
    if (accessBlocked) {
      if (getMethod(args).toUpperCase() === "GET") {
        routeToAccessBlocked(accessBlocked)
      }
      retry.fail(result.error)
    }
    if (result.error && result.error.status === 403) {
      retry.fail(result.error)
    }
    if (
      result.error &&
      ["POST", "PUT", "DELETE"].find(
        (method) => method === result.meta?.request.method,
      )
    ) {
      retry.fail(result.error)
    }
    return result
  },
  {
    maxRetries: 5,
  },
)
